import { useEffect, useMemo, useRef, useState } from 'react'
import type { LiveTranscriptTurn } from '@/utils/liveTranscriptTurns'

// Per-word cadence of the DENSER stream in a batch. The sparser stream slows to
// match so both start and finish together (see `useSequentialBatchReveal`).
// This is the single speed knob for the enhanced-mode reveal. Pushed as low as
// 20ms in an earlier pass to outrun batch arrivals, but that made the reveal
// too fast to actually perceive turn-by-turn — batches finished revealing
// within a couple hundred ms, so every turn effectively popped in fully
// formed anyway, defeating the point of streaming at all. Set to 70ms
// (fast reading pace) so a turn's text remains watchable as it streams in;
// `MAX_BACKLOG_MS` below is the actual defense against a real
// backlog, not raw speed.
const BASE_MS_PER_WORD = 70

// Upper bound on how far the reveal is allowed to lag behind arrival. A fixed
// per-word cadence combined with strictly-sequential batches has no ceiling on
// its own — on a long, busy meeting the queue of un-revealed batches keeps
// growing and the visible lag keeps compounding for the entire meeting
// (BRAI-298). Whenever the remaining queue would take longer than this to
// animate at the normal cadence, older batches jump straight to fully
// revealed instead of animating, so the lag never exceeds this bound. Kept
// well under the original 30s ceiling (which still let a visible "dồn toa"
// pile up before catching up) so a genuine burst gets flushed quickly, while
// the readable 70ms cadence above is left free to run at its own pace for
// the common, unhurried case.
export const MAX_BACKLOG_MS = 8_000

/**
 * Finds the first index whose full reveal duration, if animated normally
 * from there to the end of the list, would still land within `maxBacklogMs`.
 * Everything before that index has accumulated too much backlog and should
 * be skipped straight to fully revealed. Always keeps at least the last item
 * as the "active" one, even if its own duration alone exceeds the budget.
 *
 * @param durationsMs Full reveal duration (ms) of each still-pending item, oldest first.
 */
export function findCatchUpStartIndex(durationsMs: number[], maxBacklogMs: number): number {
  let total = 0
  let start = durationsMs.length - 1
  for (let i = durationsMs.length - 1; i >= 0; i -= 1) {
    const next = total + durationsMs[i]
    if (next > maxBacklogMs && i !== durationsMs.length - 1) break
    total = next
    start = i
  }
  return Math.max(start, 0)
}

/**
 * Splits text into word chunks, keeping each word's preceding whitespace
 * attached to it (rather than as a separate chunk) so re-joining any prefix of
 * the chunks reproduces the original spacing exactly — matching the
 * leading-space-per-segment convention used by `createTranslationBatchTokens`
 * in `useProxySttRecording`.
 */
export function splitIntoWordChunks(text: string): string[] {
  const chunks: string[] = text.match(/\s*\S+/g) ?? []
  const consumedLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
  if (consumedLength < text.length) {
    // Trailing whitespace with no following non-space content — keep it so the
    // full text is still reproduced once fully revealed.
    chunks.push(text.slice(consumedLength))
  }
  return chunks
}

/** Revealed prefix state for one batch turn, ready for presentational render. */
export interface RevealedBatch {
  /** Revealed source-transcript prefix, or `undefined` when nothing is shown yet. */
  revealedSourceText: string | undefined
  /** True once the whole source transcript for this turn is revealed. */
  sourceIsFinal: boolean
  /** Revealed batch-translation prefix, or `undefined` when nothing is shown yet. */
  revealedTranslationText: string | undefined
  /** True once the whole batch translation for this turn is revealed. */
  translationIsFinal: boolean
  /** True for the single batch currently animating. */
  isActive: boolean
}

interface TurnChunks {
  source: string[]
  translation: string[]
  /**
   * True when the service already streamed this turn word by word. Such a turn
   * costs zero ticks and renders whatever has arrived, immediately — animating
   * it would delay the same text a second time.
   */
  streamed: boolean
  /** False while the service is still sending words for a streamed turn. */
  streamDone: boolean
}

/**
 * Drives a strictly-sequential, duration-synced word-by-word reveal across an
 * incrementally-growing list of batch turns, for the enhanced-mode bilingual
 * timeline. Batch deliveries are atomic (the backend sends one complete segment
 * per event), so this purely rendering-layer effect fakes the streaming.
 *
 * Guarantees:
 * - **Sequential batches**: batch N is fully revealed before batch N+1 starts;
 *   a single shared cursor (`completedCount` + `activeTick`) and one timer drive
 *   the whole list, so batches never overlap.
 * - **Synced streams within a batch**: source and translation share ONE progress
 *   fraction `p = tick / N` (where `N = max(sourceWords, translationWords)`), so
 *   both start at `p = 0` and both reach full text at `p = 1` at the same tick,
 *   even when their word counts differ. The denser stream advances ~one word per
 *   tick; the sparser stream advances fractionally and lands on the last word in
 *   lockstep.
 *
 * Only turns `0 .. completedCount` are returned (all completed batches plus the
 * single active one); future batches are omitted until reached, producing a
 * clean one-row-at-a-time cascade. Already-revealed batches return byte-identical
 * strings every tick so a memoized row never re-renders.
 *
 * @param batchTurns Turns to reveal, already filtered to those with a batch
 *   translation. Should be referentially stable across internal ticks (memoize
 *   in the caller) so the driver is not needlessly rescheduled.
 * @returns One `RevealedBatch` per currently-visible turn, index-aligned with
 *   `batchTurns`.
 */
export function useSequentialBatchReveal(batchTurns: LiveTranscriptTurn[]): RevealedBatch[] {
  const chunksByTurn = useMemo<TurnChunks[]>(
    () =>
      batchTurns.map((turn) => ({
        source: splitIntoWordChunks(turn.sourceText ?? ''),
        translation: splitIntoWordChunks(turn.batchTranslation ?? ''),
        streamed: turn.translationStreamed === true,
        streamDone: turn.translationStreamDone === true,
      })),
    [batchTurns]
  )

  const [completedCount, setCompletedCount] = useState(0)
  const [activeTick, setActiveTick] = useState(0)
  const firstIdRef = useRef<string | undefined>(batchTurns[0]?.id)

  useEffect(() => {
    // A new session replaces the turn list (different leading id) — drop the
    // cursor before the next reveal starts. Only return early when there is
    // actually progress to clear — otherwise fall through so the fresh
    // list's first batch is scheduled immediately.
    const firstId = batchTurns[0]?.id
    const isNewSession = firstId !== firstIdRef.current
    firstIdRef.current = firstId
    if (isNewSession && (completedCount !== 0 || activeTick !== 0)) {
      setCompletedCount(0)
      setActiveTick(0)
      return
    }

    // The list can also shrink WITHOUT the leading id changing — a restart
    // archives the in-flight turns then the old session's own state clears a
    // beat later (e.g. behind an `await` in the caller), briefly duplicating
    // turns before the duplicate collapses back out. That is not a new
    // session, so clamp the cursor to the new length instead of wiping it to
    // 0 — otherwise already-revealed turns would replay from scratch on
    // every restart (BRAI-298 follow-up).
    if (!isNewSession && completedCount > batchTurns.length) {
      setCompletedCount(batchTurns.length)
      setActiveTick(0)
      return
    }

    const activeIndex = completedCount
    if (activeIndex >= batchTurns.length) return // all revealed — idle, no timer

    const durationsMs = chunksByTurn
      .slice(activeIndex)
      .map(({ source, translation, streamed }, offset) => {
        if (streamed) return 0
        const totalTicks = Math.max(source.length, translation.length)
        const ticksLeft = offset === 0 ? Math.max(totalTicks - activeTick, 0) : totalTicks
        return ticksLeft * BASE_MS_PER_WORD
      })
    const catchUpOffset = findCatchUpStartIndex(durationsMs, MAX_BACKLOG_MS)
    if (catchUpOffset > 0) {
      // Backlog exceeds the budget — skip the older batches straight to fully
      // revealed and resume normal animation from the one that keeps the
      // remaining queue within budget.
      setCompletedCount(activeIndex + catchUpOffset)
      setActiveTick(0)
      return
    }

    const { source, translation, streamed } = chunksByTurn[activeIndex]
    // Zero ticks for a streamed turn: the cursor promotes it on this same pass
    // and moves straight to the next one, so a run of streamed turns is
    // consumed without ever scheduling a timer.
    const totalTicks = streamed ? 0 : Math.max(source.length, translation.length)
    if (totalTicks === 0 || activeTick >= totalTicks) {
      // Empty batch, or the active batch just finished — promote it to completed
      // and hand off to the next batch. No timer this cycle.
      setCompletedCount((count) => count + 1)
      setActiveTick(0)
      return
    }

    const timerId = window.setTimeout(() => {
      setActiveTick((tick) => tick + 1)
    }, BASE_MS_PER_WORD)
    return () => window.clearTimeout(timerId)
  }, [batchTurns, chunksByTurn, completedCount, activeTick])

  return useMemo<RevealedBatch[]>(() => {
    const renderCount = Math.min(completedCount + 1, batchTurns.length)
    const result: RevealedBatch[] = []

    for (let index = 0; index < renderCount; index += 1) {
      const { source, translation, streamed, streamDone } = chunksByTurn[index]
      const sourceCount = source.length
      const translationCount = translation.length

      // `|| streamed` is not redundant, even though the cursor promotes streamed
      // turns almost immediately. `useEffect` runs AFTER paint, so the frame
      // between a new turn arriving and the driver promoting it would render the
      // pulse placeholder — a flicker on every new turn, which is often when the
      // service is streaming. Not reachable in tests: testing-library flushes
      // effects to quiescence, so no assertion can observe that frame. Please
      // don't delete this as dead code.
      if (index < completedCount || streamed) {
        // A streamed turn is only finished when the service says so. Until its
        // `done` event lands, the translation keeps the muted styling a
        // half-revealed batch uses, so a sentence still arriving does not read
        // as a finished one — and `isActive` keeps a screen reader following it.
        const stillStreaming = streamed && !streamDone
        result.push({
          revealedSourceText: sourceCount > 0 ? source.join('') : undefined,
          sourceIsFinal: sourceCount > 0,
          revealedTranslationText: translationCount > 0 ? translation.join('') : undefined,
          translationIsFinal: translationCount > 0 && !stillStreaming,
          isActive: stillStreaming,
        })
        continue
      }

      const totalTicks = Math.max(sourceCount, translationCount)
      const progress = totalTicks === 0 ? 1 : Math.min(activeTick, totalTicks) / totalTicks
      const sourceWords = Math.round(progress * sourceCount)
      const translationWords = Math.round(progress * translationCount)

      result.push({
        revealedSourceText: sourceWords > 0 ? source.slice(0, sourceWords).join('') : undefined,
        sourceIsFinal: sourceCount > 0 && sourceWords >= sourceCount,
        revealedTranslationText:
          translationWords > 0 ? translation.slice(0, translationWords).join('') : undefined,
        translationIsFinal: translationCount > 0 && translationWords >= translationCount,
        isActive: true,
      })
    }

    return result
  }, [batchTurns.length, chunksByTurn, completedCount, activeTick])
}

export default useSequentialBatchReveal
